import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  ClipboardList,
  Factory,
  UserCheck,
  FileCheck,
  Globe
} from "lucide-react";
import Navbar from "../../components/Navbar";

const foodSafetyServices = [
  {
    title: "ISO 22000 – Food Safety Management System",
    description:
      "Implementation and certification support for ISO 22000 to ensure safe food production, handling, and distribution across the supply chain.",
    icon: ShieldCheck
  },
  {
    title: "HACCP Implementation",
    description:
      "Hazard Analysis and Critical Control Points (HACCP) system development to identify, evaluate, and control food safety hazards.",
    icon: ClipboardList
  },
  {
    title: "Food Manufacturing & Processing",
    description:
      "Certification consultancy for food manufacturing, processing, packaging, and storage facilities.",
    icon: Factory
  },
  {
    title: "Training & Awareness",
    description:
      "Employee training programs on food hygiene, GMPs, and food safety practices aligned with international standards.",
    icon: UserCheck
  },
  {
    title: "Documentation & Audit Support",
    description:
      "Complete documentation development, internal audits, gap analysis, and certification audit preparation.",
    icon: FileCheck
  },
  {
    title: "Global Compliance & Market Access",
    description:
      "Enhance customer confidence and gain access to international markets through recognized food safety certifications.",
    icon: Globe
  }
];

const FoodSafetyCertification = () => {
  return (
    <>
    <Navbar />
    <section className="bg-gray-50 py-16 sm:py-14 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto">

        {/* ================= Header ================= */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="max-w-3xl mx-auto text-center mb-14"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-[var(--bg-primary)] mb-4">
            Food Safety Certification
          </h2>
          <div className="w-20 h-1 bg-[var(--bg-primary)] mx-auto rounded-full mb-4"></div>
          <p className="text-gray-600 text-base md:text-lg">
            MBA Solutions provides end-to-end consultancy for food safety
            certifications, helping organizations comply with international
            standards and ensure safe, high-quality food products.
          </p>
        </motion.div>

        {/* ================= Cards ================= */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {foodSafetyServices.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                whileHover={{
                  y: -10,
                  scale: 1.04,
                  boxShadow: "0px 25px 50px rgba(59,130,246,0.25)"
                }}
                className="relative bg-white rounded-2xl p-7 border border-gray-100 shadow-lg group overflow-hidden"
              >
                {/* Glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-indigo-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl" />

                {/* Icon */}
                <div className="relative z-10 w-14 h-14 flex items-center justify-center rounded-xl bg-[var(--bg-primary)] text-white mb-5 shadow-md">
                  <Icon size={28} />
                </div>

                {/* Title */}
                <h3 className="relative z-10 text-lg font-bold text-gray-900 mb-2 group-hover:text-[var(--bg-primary)] transition-colors duration-300">
                  {item.title}
                </h3>

                {/* Description */}
                <p className="relative z-10 text-sm text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                  {item.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* ================= CTA ================= */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mt-16 text-center"
        >
          <p className="text-gray-600 mb-6">
            Ensure food safety, compliance, and consumer trust with globally
            recognized certifications.
          </p>
          <button className="inline-flex items-center justify-center px-8 py-3 rounded-full bg-[var(--bg-primary)] text-white font-semibold shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300">
            Get Food Safety Consultation
          </button>
        </motion.div>

      </div>
    </section>
    </>
  );
};

export default FoodSafetyCertification;
